import { getInitData } from './telegram';

const BASE = import.meta.env.VITE_API_URL ?? '';

let token: string | null = null;

export const getToken = () => token ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: any,
  ) {
    super(code);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      // Only when there is something to declare a type for: a DELETE with
      // this header and no body is rejected by the server as an empty
      // JSON body, which is how deleting anything started failing.
      ...(init.body === undefined || init.body === null
        ? {}
        : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    let code = `http_${res.status}`;
    let detail: any;
    try {
      const body = await res.json();
      code = body.error ?? code;
      detail = body.detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code, detail);
  }

  return res.status === 204 ? (undefined as T) : res.json();
}

// ---------- types ----------

export interface Me {
  id: string;
  role: 'coach' | 'client';
  firstName: string;
  credits?: number;
  /** Has ever had a package (or any credits): Book shows the balance only then. */
  hadPackage?: boolean;
  /** Their coach is switched off by the owner: no new bookings for now. */
  coachOff?: boolean;
  packages?: { id: string; balance: number; expires_at: string | null; applies_to: string[] }[];
  language?: 'en' | 'ru';
  healthConsent?: 'yes' | 'no' | 'unanswered';
  /** False while their coach has yet to approve them. */
  approved?: boolean;
  coachName?: string | null;
  bookingHorizonDays?: number;
  languageCode?: string | null;
  /** Their own lessons ahead on credits, and how they split (slice 2); null when there are none. */
  coverage?: { planned: number; covered: number; needsPayment: number; cashDue: number } | null;
  /** Credits are spent when the lesson happens: Book asks the server what a pick costs. */
  creditOnUse?: boolean;
}

export interface SessionType {
  id: string;
  kind: 'private' | 'group';
  name: string;
  description: string | null;
  duration_min: number;
  default_capacity: number;
  credit_cost: number;
  cash_price: number | null;
}

export interface SlotResponse {
  durationMin: number;
  creditCost: number;
  /** hot: the coach marked this time 🔥 (same price, it just stands out). */
  slots: { startsAt: string; endsAt: string; hot?: boolean }[];
}

export interface ClassItem {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  capacity: number;
  credit_cost: number;
  location: string | null;
  type_name: string;
  booked: number;
  waitlisted: number;
  seats_left: number;
  my_status: 'booked' | 'waitlisted' | null;
}

export interface Booking {
  id: string;
  status: string;
  waitlist_position: number | null;
  credits_charged: number;
  /** What the credits part is worth in euros, on a lesson paid partly in credits. */
  credit_value?: number | null;
  payment_mode: 'credits' | 'cash';
  amount_due: number | null;
  series_id: string | null;
  client_note: string | null;
  session_id: string;
  kind: 'private' | 'group';
  starts_at: string;
  ends_at: string;
  location: string | null;
  title: string;
  free_cancel_until: string;
  /** A future booking on credits: covered by the package, or paid at the lesson (slice 2). */
  pay?: 'covered' | 'due' | 'legacy' | 'legacy_partial' | null;
  pay_due?: number | null;
}

export interface LedgerEntry {
  id: string;
  delta: number;
  reason: string;
  created_at: string;
  expires_at: string | null;
  session_title: string | null;
  session_starts_at: string | null;
}

// ---------- calls ----------

/**
 * Sign-in. initData is exchanged for our own token only on a fresh launch:
 * the server refuses initData older than 5 minutes or seen before, and
 * Telegram hands a reloaded Mini App the same initData again. So the token
 * is kept in sessionStorage (this webview only) and reused on a reload
 * while it is valid; a token that is expired or refused (401) is dropped
 * and initData tried once instead.
 *
 * One sign-in per page: React's StrictMode runs the start-up effect twice
 * in development, and a second exchange of the same initData would be
 * refused as a replay.
 */
const STORED = 'cb.session';
let signingIn: Promise<Me> | null = null;

/** The Telegram user the current initData is for, so a stored token is only reused for them. */
function launchUserId(): string | null {
  try {
    const u = new URLSearchParams(getInitData()).get('user');
    return u ? String(JSON.parse(u).id) : null;
  } catch { return null; }
}

function readStored(): string | null {
  try {
    const raw = sessionStorage.getItem(STORED);
    if (!raw) return null;
    const v = JSON.parse(raw) as { token?: string; user?: string };
    return v.token && v.user && v.user === launchUserId() ? v.token : null;
  } catch { return null; }
}
function writeStored(t: string | null) {
  try {
    if (t) sessionStorage.setItem(STORED, JSON.stringify({ token: t, user: launchUserId() }));
    else sessionStorage.removeItem(STORED);
  } catch { /* storage unavailable: sign in with initData each launch */ }
}

/** A session handed back by coach sign-up: used and kept like a sign-in. */
export function adoptSession(t: string) {
  token = t;
  writeStored(t);
}

export function authenticate(): Promise<Me> {
  if (!signingIn) {
    signingIn = signIn().catch((err) => { signingIn = null; throw err; });
  }
  return signingIn;
}

async function signIn(): Promise<Me> {
  const stored = readStored();
  if (stored) {
    token = stored;
    try {
      return await getMe();
    } catch (err) {
      // Expired or refused: forget it and sign in afresh. Anything else
      // (offline, server down) is a real error.
      if (!(err instanceof ApiError && err.status === 401)) throw err;
      token = null;
      writeStored(null);
    }
  }
  const res = await request<{ token: string; user: Me }>('/api/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ initData: getInitData() }),
  });
  token = res.token;
  writeStored(res.token);
  return getMe();
}

export const getMe = () => request<Me>('/api/me');
export const getSessionTypes = () => request<SessionType[]>('/api/session-types');

export const getSlots = (sessionTypeId: string, from: string, to: string) =>
  request<SlotResponse>(
    `/api/slots?session_type_id=${sessionTypeId}&from=${from}&to=${to}`,
  );

export const getClasses = () => request<ClassItem[]>('/api/classes');

export const getBookings = (past = false) =>
  request<Booking[]>(`/api/bookings?past=${past}`);

/**
 * What these picks would cost if booked now, walked in among everything
 * already booked (slice 2). `displaced`: lessons already booked that the
 * picks would push off the package.
 */
export const coverPreview = (lessons: ({ sessionTypeId: string; startsAt: string } | { sessionId: string })[]) =>
  request<{
    lessons: { covered: boolean; cashDue: number | null }[];
    displaced: { startsAt: string; cashDue: number | null }[];
  }>('/api/bookings/cover-preview', { method: 'POST', body: JSON.stringify({ lessons }) });

export const getMyCalendarUrl = () =>
  request<{ url: string; webcal: string }>('/api/my-calendar-url');

export interface AxisProgress {
  axis_id: string; name: string; name_ru: string | null; sort_order: number;
  score: number; rated_by: string; score_before: number | null;
  /** sql/046: padel or tennis. */
  sport?: 'padel' | 'tennis';
}

export const getMyProgress = (clientId: string) =>
  request<AxisProgress[]>(`/api/ratings/client/${clientId}/progress`);

export const getMyMatches = () => request<any[]>('/api/matches/mine');
/** The player's journal: their past lessons and goals, never the coach's private notes. */
/** The player's own level, skills and last four weeks. See /api/me/profile. */
export interface MyProfileData {
  /** The coach's sports, and the player's (null unless the coach teaches tennis too). */
  sport?: { coach: string[]; client: 'padel' | 'tennis' | 'both' | null };
  level: { value: number; setAt: string; setBy: string | null } | null;
  skills: { axisId: string; name: string; name_ru: string | null; score: number; change: number | null; trend: number[]; sport?: string }[];
  weeks: { from: string; today: string; days: { day: string; lessons: number; noShows: number }[] };
}
export const getMyProfile = () => request<MyProfileData>('/api/me/profile');

export const getMyJournal = () => request<import('../components/Journal').JournalData>('/api/me/journal');
export const logMatch = (body: Record<string, unknown>) =>
  request<{ id: string; reviewFee: number | null }>('/api/matches',
    { method: 'POST', body: JSON.stringify(body) });
/** Correcting one, which the server allows only until it is reviewed. */
export const editMatch = (id: string, body: Record<string, unknown>) =>
  request<{ saved: boolean }>(`/api/matches/${id}`,
    { method: 'PUT', body: JSON.stringify(body) });
export const getCreditHistory = () => request<LedgerEntry[]>('/api/credits/history');

export const bookPrivate = (
  sessionTypeId: string,
  startsAt: string,
  paymentMode: 'credits' | 'cash',
) =>
  request<{ bookingId: string; creditsRemaining: number; amountDue: number | null; creditsCharged?: number; creditValue?: number | null }>(
    '/api/bookings/private',
    {
      method: 'POST',
      body: JSON.stringify({
        session_type_id: sessionTypeId,
        starts_at: startsAt,
        payment_mode: paymentMode,
      }),
    },
  );

export const bookRecurring = (
  sessionTypeId: string, startsAt: string, weeks: number, paymentMode: 'credits' | 'cash',
) =>
  request<{
    seriesId: string;
    booked: { startsAt: string }[];
    skipped: { startsAt: string; reason: string }[];
    creditsRemaining: number;
    amountDue: number | null;
  }>('/api/bookings/recurring', {
    method: 'POST',
    body: JSON.stringify({
      session_type_id: sessionTypeId, starts_at: startsAt,
      weeks, payment_mode: paymentMode,
    }),
  });

export const cancelSeries = (seriesId: string) =>
  request<{ cancelled: number; refunded: number; message: string }>(
    `/api/bookings/series/${seriesId}/cancel`, { method: 'POST', body: JSON.stringify({}) });

export const bookClass = (sessionId: string) =>
  request<{ bookingId: string; status: string; waitlistPosition?: number }>(
    '/api/bookings/class',
    { method: 'POST', body: JSON.stringify({ session_id: sessionId }) },
  );

export const updateBookingNote = (bookingId: string, note: string) =>
  request<{ id: string; client_note: string | null }>(
    `/api/bookings/${bookingId}/note`,
    { method: 'PUT', body: JSON.stringify({ note }) });

export const inviteFriend = (bookingId: string, username: string, payer: 'me' | 'them') =>
  request<{ message: string; link: string; expiresAt: string }>(
    `/api/bookings/${bookingId}/invite`,
    { method: 'POST', body: JSON.stringify({ username, payer }) });

export const setLanguage = (language: 'en' | 'ru') =>
  request<{ language: 'en' | 'ru' }>('/api/language',
    { method: 'PUT', body: JSON.stringify({ language }) });

export const answerHealthConsent = (consent: boolean) =>
  request<{ healthConsent: 'yes' | 'no' | 'unanswered' }>(
    '/api/health-consent', { method: 'POST', body: JSON.stringify({ consent }) });


export const rescheduleBooking = (bookingId: string, startsAt: string) =>
  request<{ moved: boolean; startsAt: string }>(
    `/api/bookings/${bookingId}/reschedule`,
    { method: 'POST', body: JSON.stringify({ starts_at: startsAt }) });

export const cancelBooking = (bookingId: string) =>
  request<{ refunded: number; lateCancel: boolean; message: string }>(
    `/api/bookings/${bookingId}/cancel`,
    { method: 'POST', body: JSON.stringify({}) },
  );
